import { EvaChartStates } from "types/factory";

/**
 * Derives a log-axis minimum from series data: the largest power of 10 at or
 * below the smallest positive y value across all series. Non-positive values
 * are ignored since they can't appear on a log axis.
 *
 * Used as a client-side fallback for charts (e.g. Cross Plot) that don't receive
 * a backend-provided `layout.yAxis.logMin`. ECharts won't auto-range a log axis
 * from scatter data on its own (it defaults to [1, 10]), so an explicit floor is
 * required.
 *
 * @param series array of series objects from the chart response
 * @returns the power-of-10 floor, or undefined when no positive y value exists
 */
const getLogMinFromSeries = (series: EvaChartStates["series"]): number | undefined => {
  let minPositiveY = Infinity;
  for (const s of series) {
    for (const value of s.y) {
      if (typeof value === "number" && value > 0 && value < minPositiveY) {
        minPositiveY = value;
      }
    }
  }
  if (!Number.isFinite(minPositiveY)) {
    return undefined;
  }
  // Build the power of 10 from its exponent (e.g. 1e-4) rather than
  // Math.pow(10, -4), which introduces floating-point noise (0.00009999…).
  const exponent = Math.floor(Math.log10(minPositiveY));
  return Number(`1e${exponent}`);
};

export default getLogMinFromSeries;
